"use strict";
/**
 * useStrategyAlignment
 *
 * Encapsulates the lifecycle of fetching a Strategy → Use Case alignment as
 * a discriminated-union state machine: `idle` → `loading` → `success | error`.
 *
 * Trust-boundary enforcement:
 *  - Inputs are debounced so rapid selection changes do not amplify into
 *    server-side compute load.
 *  - Stale resolutions are dropped via a `cancelled` flag (the underlying
 *    request is not aborted yet — see follow-up to thread `AbortSignal`
 *    through the shared apiPost helper for a repo-wide fix).
 *  - The wire payload is re-validated by {@link validateAlignmentResponse}
 *    before transitioning to `success`, regardless of the static return type.
 *  - An optional `onTelemetry` callback receives every error transition for
 *    monitoring; schema mismatches are reported as the highest-signal event
 *    because they imply client/server skew or response tampering.
 *
 * @internal The barrel re-export deliberately omits the `Options` arg —
 * it is currently consumed only by the orchestrator inside this folder.
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useStrategyAlignment = void 0;
var react_1 = require("react");
var ai_hub_strategy_api_service_1 = __importDefault(require("../../shared/services/ai-hub-strategy-api.service"));
// Direct intra-feature import: avoids pulling the whole utils barrel
// (and its Office Fabric dependency) into the hook's module graph.
var use_case_alignment_utils_1 = require("../utils/use-case-alignment-utils");
var DEFAULT_ERROR = 'Failed to generate alignment. Please try again.';
var MALFORMED_RESPONSE_ERROR = 'The alignment service returned an unexpected response. Please contact support if the problem persists.';
var DEFAULT_DEBOUNCE_MS = 200;
var IDLE = { status: 'idle' };
/**
 * Fetch and manage Strategy ↔ Use Case alignment state.
 *
 * @param workflowId - Workflow context for the selected use case (empty string when unknown).
 * @param strategyId - Selected strategy ID (empty string when none).
 * @param useCaseId - Selected use case ID (empty string when none).
 * @param options - Optional debounce + telemetry overrides.
 * @returns Discriminated union representing current alignment state.
 */
exports.useStrategyAlignment = function (workflowId, strategyId, useCaseId, options) {
    if (options === void 0) { options = {}; }
    var _a = options.debounceMs, debounceMs = _a === void 0 ? DEFAULT_DEBOUNCE_MS : _a, onTelemetry = options.onTelemetry;
    var _b = react_1.useState(IDLE), state = _b[0], setState = _b[1];
    // Keep the latest telemetry callback in a ref so changing identity does
    // not retrigger the fetch effect.
    var onTelemetryRef = react_1.useRef(onTelemetry);
    react_1.useEffect(function () {
        onTelemetryRef.current = onTelemetry;
    }, [onTelemetry]);
    react_1.useEffect(function () {
        if (!workflowId || !strategyId || !useCaseId) {
            setState(IDLE);
            return;
        }
        var cancelled = false;
        var debounceHandle = window.setTimeout(function () {
            setState({ status: 'loading' });
            (function () { return __awaiter(void 0, void 0, void 0, function () {
                var raw, data, error_1, message;
                var _a, _b;
                return __generator(this, function (_c) {
                    switch (_c.label) {
                        case 0:
                            _c.trys.push([0, 2, , 3]);
                            return [4 /*yield*/, ai_hub_strategy_api_service_1.default.getUseCaseAlignment(workflowId, strategyId, useCaseId)];
                        case 1:
                            raw = _c.sent();
                            if (cancelled) {
                                return [2 /*return*/];
                            }
                            data = use_case_alignment_utils_1.validateAlignmentResponse(raw);
                            if (!data) {
                                (_a = onTelemetryRef.current) === null || _a === void 0 ? void 0 : _a.call(onTelemetryRef, { type: 'schema-mismatch' });
                                setState({ status: 'error', error: MALFORMED_RESPONSE_ERROR });
                                return [2 /*return*/];
                            }
                            setState({ status: 'success', data: data });
                            return [3 /*break*/, 3];
                        case 2:
                            error_1 = _c.sent();
                            if (cancelled) {
                                return [2 /*return*/];
                            }
                            message = error_1 instanceof Error ? error_1.message : DEFAULT_ERROR;
                            (_b = onTelemetryRef.current) === null || _b === void 0 ? void 0 : _b.call(onTelemetryRef, { type: 'fetch-failed', message: message });
                            setState({ status: 'error', error: message });
                            return [3 /*break*/, 3];
                        case 3: return [2 /*return*/];
                    }
                });
            }); })();
        }, Math.max(0, debounceMs));
        return function () {
            cancelled = true;
            window.clearTimeout(debounceHandle);
        };
    }, [workflowId, strategyId, useCaseId, debounceMs]);
    var clearError = react_1.useCallback(function () { return setState(IDLE); }, []);
    return state.status === 'error' ? __assign(__assign({}, state), { clearError: clearError }) : state;
};
